import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Table, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { downloadExport } from '../../../domain/tableExport.js';
import FeeCell, { FeeRateCell } from '../../../components/FeeCell.jsx';
import { requestType } from '../../../domain/requestType.js';
import { formatMoney, formatUnits } from '../../../format';
import RegisterFilters, { rangeDates } from './RegisterFilters.jsx';
import { SOURCE_LABEL, SOURCE_OPTIONS, TXN_TYPE_COLOUR, TXN_TYPE_LABEL, TXN_TYPE_OPTIONS, transactionsExportQuery, transactionsQuery } from './register.js';
import { usePagedList } from './usePagedList.js';

const { Text } = Typography;

const dash = (value, format = (v) => v) => (value === null || value === undefined || value === '' ? '—' : format(value));

/**
 * The register's unit transactions as GET register/transactions answers them,
 * in the register's own order: units are signed (out is negative); the source
 * says how the row came - a LIV order, the INITIAL load's opening lots
 * (CONVERSION) or a DAILY upload (UPLOAD, its TRANSACTION_ID as the
 * reference; IP-110). The columns are the design's (Figma "Table:margin"):
 * the balance after, the posting's people and the upload's own key are left
 * to the holder's drawer. Rendered by TransactionHistory, and on its own by
 * UnitRegistry.test.js.
 */
export const TransactionsTable = ({ items, loading = false, onHolder }) => {
    const columns = [
        { title: 'Effective date', dataIndex: 'dealingDate', key: 'dealingDate', width: 106 },
        { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 65 },
        {
            title: 'Holder',
            dataIndex: 'holderNo',
            key: 'holderNo',
            width: 84,
            render: (v, t) => (onHolder && t.holderId
                ? <button type="button" className="register-holder-link" onClick={() => onHolder({ holderId: t.holderId, holderNo: v })}>{v}</button>
                : dash(v))
        },
        {
            title: 'Type',
            dataIndex: 'txnType',
            key: 'txnType',
            width: 164,
            render: (v) => <span className={'register-type is-' + (TXN_TYPE_COLOUR[v] ?? 'default')}>{TXN_TYPE_LABEL[v] ?? v}</span>
        },
        // LKR based or unit based: the side the investor fixed (none for an upload or a conversion).
        { title: 'Request type', key: 'requestType', width: 104, render: (_, t) => dash(requestType(t)) },
        { title: 'Source', dataIndex: 'source', key: 'source', width: 79, render: (v) => SOURCE_LABEL[v] ?? dash(v) },
        { title: 'Transaction ref', dataIndex: 'transactionRef', key: 'transactionRef', width: 231, render: (v) => dash(v) },
        // IP-202: the strategy activation code beside every transaction.
        { title: 'Strategy code', dataIndex: 'strategyActivationCode', key: 'strategyActivationCode', width: 130, render: (v) => dash(v) },
        { title: 'Units', dataIndex: 'units', key: 'units', width: 83, render: (v) => formatUnits(v) },
        { title: 'Price', dataIndex: 'price', key: 'price', width: 78, render: (v) => dash(v, formatUnits) },
        { title: 'Amount (LKR)', dataIndex: 'amount', key: 'amount', width: 120, render: (v) => dash(v, formatMoney) },
        // The fee, and the % it was charged at in its own column (components/FeeCell.jsx): the review's own rate is marked.
        { title: 'Fee (LKR)', dataIndex: 'feeAmount', key: 'feeAmount', width: 86, render: (_, t) => <FeeCell row={t} showRate={false} /> },
        { title: 'Fee %', key: 'feeRate', width: 170, render: (_, t) => <FeeRateCell row={t} /> }
    ];
    return (
        <Table rowKey="txnId" className="register-table register-txn-table" loading={loading} columns={columns} dataSource={items} pagination={false}
            scroll={{ x: 1100 }} locale={{ emptyText: 'No transactions match' }} />
    );
};

/**
 * The register's transactions, filtered by fund, holder, type, source and
 * dealing date, a page at a time with Load more (register.js
 * transactionsQuery). The fund filter is the route's fund (onFund changes the
 * route); a holder chosen from a drawer or a row is that holder (onHolder),
 * and the holder box matches a holder number as it is typed.
 */
const HOLDER_FILTER_DELAY = 300;

const TransactionHistory = ({ funds, fundId, onFund, holder, onHolder }) => {
    const { api } = useAccess();
    const { message } = App.useApp();
    const [downloading, setDownloading] = useState(false);
    const [txnType, setTxnType] = useState(null);
    const [source, setSource] = useState(null);
    const [range, setRange] = useState(null);
    const [holderText, setHolderText] = useState('');
    const [holderNo, setHolderNo] = useState('');
    const { from, to } = rangeDates(range);
    const holderId = holder?.holderId ?? null;

    // The box updates as it is typed; the list is read again once the text settles.
    useEffect(() => {
        const handle = setTimeout(() => setHolderNo(holderText.trim()), HOLDER_FILTER_DELAY);
        return () => clearTimeout(handle);
    }, [holderText]);

    const fetchPage = useCallback(
        (cursor) => api.register.transactions(transactionsQuery({ fundId, holderId, holderNo, txnType, source, from, to, cursor })),
        [api, fundId, holderId, holderNo, txnType, source, from, to]
    );
    const list = usePagedList(fetchPage, 'txnId');

    // Every row the table's filters match, not the page shown: the same filters, asked of the export route (Excel).
    const download = async () => {
        setDownloading(true);
        try {
            await downloadExport({
                call: () => api.register.transactionsExport(transactionsExportQuery({ fundId, holderId, holderNo, txnType, source, from, to })),
                fallbackName: 'transactions_' + (fundId ?? 'ALL') + '.xlsx',
                message,
                what: 'The transactions',
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <RegisterFilters
                funds={funds} fundId={fundId} onFund={onFund} holder={holder} onHolder={onHolder}
                holderNo={holderText} onHolderNo={setHolderText}
                txnType={txnType} typeOptions={TXN_TYPE_OPTIONS} onType={setTxnType}
                source={source} sourceOptions={SOURCE_OPTIONS} onSource={setSource}
                range={range} onRange={setRange}
                download downloading={downloading} onDownload={download}
                onClear={() => { setTxnType(null); setSource(null); setRange(null); setHolderText(''); setHolderNo(''); onHolder(null); }}
            />
            {list.error && <Alert type="error" showIcon message={list.error} className="auth-alert" closable onClose={list.clearError} />}
            <TransactionsTable items={list.items} loading={list.loading} onHolder={onHolder} />
            <div className="register-list-footer">
                <Text type="secondary">{list.items.length} shown{list.nextCursor ? ', more to load' : ''}</Text>
                {list.nextCursor && <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>}
            </div>
        </>
    );
};

export default TransactionHistory;
